// The first-push notice of the date-based table: the stored choice "Do not show this again".
// The choice lives in the browser (localStorage), one key per user id, so a second user of the same
// browser still sees the notice. Cancel stores nothing; only Submit with the box ticked stores the choice.

export const DIRECT_SUBMIT_NOTICE_KEY_PREFIX = 'roompulse.rates.table.direct-submit-notice';

export type DirectSubmitNoticeStorage = {
	getItem: <T>(name: string) => T | null,
	setItem: <T>(name: string, value: T) => void,
};

export type DirectSubmitNoticeChoice = { dismissedAt: string };

export type DirectSubmitNoticeRequest = {
	// "Thu 01/10"
	dateLabel: string,
	// The rooms that the push carries: the title reads "Send 5 rates now?"
	rateCount: number,
	// "5 rooms · Standard"
	detail: string,
};

export const directSubmitNoticeKey = (userId: number | undefined | null): string =>
	`${DIRECT_SUBMIT_NOTICE_KEY_PREFIX}.${userId ?? 'anonymous'}`;

export const isDirectSubmitNoticeDismissed = (storage: DirectSubmitNoticeStorage, userId: number | undefined | null): boolean => {
	if (userId == null) return false;
	try {
		const choice = storage.getItem<DirectSubmitNoticeChoice>(directSubmitNoticeKey(userId));
		return typeof choice?.dismissedAt === 'string' && choice.dismissedAt !== '';
	} catch {
		return false;
	}
};

// The rows of the table share the choice: a store of the change, so every plane follows the first Submit.
const listeners = new Set<() => void>();
export const subscribeDirectSubmitNotice = (listener: () => void): (() => void) => {
	if (listeners.size === 0 && typeof window !== 'undefined') window.addEventListener('storage', onStorageChange);
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
		if (listeners.size === 0 && typeof window !== 'undefined') window.removeEventListener('storage', onStorageChange);
	};
};
const notifyDirectSubmitNotice = () => { listeners.forEach(listener => listener()); };
const onStorageChange = (event: StorageEvent) => {
	if (event.key === null || event.key.startsWith(DIRECT_SUBMIT_NOTICE_KEY_PREFIX + '.')) notifyDirectSubmitNotice();
};

export const dismissDirectSubmitNotice = (storage: DirectSubmitNoticeStorage, userId: number | undefined | null, now: Date = new Date()): void => {
	if (userId == null) return;
	try {
		storage.setItem<DirectSubmitNoticeChoice>(directSubmitNoticeKey(userId), { dismissedAt: now.toISOString() });
	} catch {
		// A blocked storage (private mode, quota) keeps the notice; the push still goes out.
	}
	notifyDirectSubmitNotice();
};

// The detail line under the text: "5 rooms · Standard" or "8 rooms · Standard · Premium".
export const directSubmitNoticeDetail = (rateCount: number, roomTypeNames: readonly string[]): string =>
	[`${rateCount} ${rateCount === 1 ? 'room' : 'rooms'}`, ...roomTypeNames].join(' · ');

// The title of the notice.
export const directSubmitNoticeTitle = (rateCount: number): string =>
	`Send ${rateCount} ${rateCount === 1 ? 'rate' : 'rates'} now?`;
